import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Collapse, Typography} from '@mui/material';

import {TargetProfileDetailField} from './TargetProfileDetailField';
import {TargetProfileDialogSectionCard} from './TargetProfileDialogSectionCard';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {getSeniorityLevelLabel} from '../../../../../utils/getSeniorityLevelLabel';
import type {TargetProfileDetail} from '../../types/DepartmentTargetProfileTypes';

export interface Props {
  profile: TargetProfileDetail;
}

export const TargetProfileDialogOverviewSection = ({profile}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal.targetProfiles'});
  const {t: tDetail} = useTranslation('translation', {
    keyPrefix: 'companySettings.departments.modal.targetProfiles.detail',
  });
  const {t: tShared} = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const metaItems = useMemo(() => {
    return [
      profile.seniority
        ? {label: tDetail('seniority'), value: getSeniorityLevelLabel(profile.seniority, tShared)}
        : null,
      profile.experienceLevel ? {label: tDetail('experienceLevel'), value: profile.experienceLevel} : null,
      profile.industry ? {label: tDetail('industry'), value: profile.industry} : null,
      profile.geography ? {label: tDetail('geography'), value: profile.geography} : null,
    ].filter((item): item is {label: string; value: string} => item !== null);
  }, [profile, tDetail, tShared]);

  const occupationDescription =
    currentLanguage === 'de' ? profile.occupation.descriptionDe : profile.occupation.descriptionEn;
  const aliases = profile.occupationAliases[currentLanguage] ?? [];
  const hasDisclosureContent = Boolean(
    occupationDescription?.trim() ||
      profile.educationRequirements?.trim() ||
      profile.workingStyleNotes?.trim() ||
      aliases.length > 0
  );

  return (
    <TargetProfileDialogSectionCard title={String(t('viewDialog.overview'))}>
      <Typography variant='subtitle1' sx={{fontWeight: 700, color: greenColors[900], mb: 0.5}}>
        {profile.title}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{mb: metaItems.length > 0 ? 1 : 0}}>
        {profile.professionalArea.name[currentLanguage]}
      </Typography>

      {metaItems.length > 0 && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: hasDisclosureContent ? 1 : 0}}>
          {metaItems.map((item) => (
            <Typography key={item.label} variant='body2'>
              <Box component='span' sx={{fontWeight: 600, color: greenColors[700]}}>
                {item.label}:
              </Box>{' '}
              {item.value}
            </Typography>
          ))}
        </Box>
      )}

      {hasDisclosureContent && (
        <>
          <Typography
            component='button'
            type='button'
            variant='body2'
            onClick={() => setDetailsOpen((open) => !open)}
            sx={{
              border: 'none',
              background: 'none',
              p: 0,
              cursor: 'pointer',
              color: greenColors[700],
              fontWeight: 600,
              textAlign: 'left',
              mb: detailsOpen ? 1 : 0,
            }}
          >
            {detailsOpen ? t('viewDialog.hideDetails') : t('viewDialog.moreDetails')}
          </Typography>
          <Collapse in={detailsOpen}>
            <Box>
              {occupationDescription?.trim() && (
                <Typography variant='body2' color='text.secondary' sx={{mb: 1.5, whiteSpace: 'pre-wrap'}}>
                  {occupationDescription}
                </Typography>
              )}
              <TargetProfileDetailField
                label={tDetail('educationRequirements')}
                value={profile.educationRequirements}
              />
              <TargetProfileDetailField label={tDetail('workingStyleNotes')} value={profile.workingStyleNotes} />
              {aliases.length > 0 && (
                <Box>
                  <Typography
                    variant='caption'
                    sx={{color: greenColors[700], fontWeight: 600, display: 'block', mb: 0.5}}
                  >
                    {tDetail('aliases')}
                  </Typography>
                  <Typography variant='body2'>{aliases.map((entry) => entry.alias).join(', ')}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </>
      )}
    </TargetProfileDialogSectionCard>
  );
};
